/**
 * ScheduleBuilder — selector de horario amigable (PR-CMgmt-H).
 *
 * Reemplaza el input de cron crudo del wizard. UX:
 *   1. Tabs presets fijos (Diario 3 AM, Cada hora, Cada 6 h, Cada 12 h,
 *      Semanal Lunes 3 AM)
 *   2. Botón "Personalizado" → abre checkboxes de días (L M M J V S D)
 *      + selector de hora HH:MM. Genera el cron automáticamente.
 *   3. Preview legible debajo: "⚡ Se ejecutará lunes y miércoles a las 03:00 AM".
 *
 * El componente emite `cron` ya generado por `onChange(cron)`. El parent
 * solo guarda el string final.
 */
import { useEffect, useMemo, useState } from "react";
import { Clock } from "lucide-react";

import { cn } from "@/lib/utils";

interface ScheduleBuilderProps {
  /** Cron actual. Si no matchea ningún preset, el modo arranca en "custom". */
  value: string;
  onChange: (cron: string) => void;
}

interface Preset {
  label: string;
  cron: string;
  hint: string;
}

const PRESETS: Preset[] = [
  {
    label: "Diario · 3:00 AM",
    cron: "0 3 * * *",
    hint: "Una vez al día a las 03:00",
  },
  { label: "Cada hora", cron: "0 * * * *", hint: "Al minuto :00 de cada hora" },
  {
    label: "Cada 6 horas",
    cron: "0 */6 * * *",
    hint: "A las 00:00, 06:00, 12:00, 18:00",
  },
  { label: "Cada 12 horas", cron: "0 */12 * * *", hint: "A las 00:00 y 12:00" },
  {
    label: "Semanal · Lunes 3:00 AM",
    cron: "0 3 * * 1",
    hint: "Cada lunes a las 03:00",
  },
];

const DAY_LABELS = ["L", "M", "M", "J", "V", "S", "D"]; // cron 1..7 (L=1 ... D=0/7)
const DAY_NAMES = [
  "lunes",
  "martes",
  "miércoles",
  "jueves",
  "viernes",
  "sábado",
  "domingo",
];
const DAY_CRON_NUMS = ["1", "2", "3", "4", "5", "6", "0"]; // L..D mapeo cron

function buildCron(days: boolean[], hh: number, mm: number): string {
  const allDays = days.every(Boolean);
  const noDays = days.every((d) => !d);
  if (noDays) return ""; // inválido
  const dayList = allDays
    ? "*"
    : days
        .map((d, i) => (d ? DAY_CRON_NUMS[i] : null))
        .filter((v): v is string => v !== null)
        .join(",");
  return `${mm} ${hh} * * ${dayList}`;
}

function parseCustomCron(
  cron: string,
): { days: boolean[]; hh: number; mm: number } | null {
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return null;
  const [mm, hh, dom, mon, dow] = parts;
  if (dom !== "*" || mon !== "*") return null;
  const mmNum = parseInt(mm, 10);
  const hhNum = parseInt(hh, 10);
  if (isNaN(mmNum) || isNaN(hhNum)) return null;
  if (mmNum < 0 || mmNum > 59 || hhNum < 0 || hhNum > 23) return null;
  const days = new Array(7).fill(false) as boolean[];
  if (dow === "*") {
    days.fill(true);
  } else {
    const nums = dow.split(",");
    for (const n of nums) {
      const idx = DAY_CRON_NUMS.indexOf(n.trim());
      if (idx === -1) return null;
      days[idx] = true;
    }
  }
  return { days, hh: hhNum, mm: mmNum };
}

export function describeCron(cron: string): string {
  const parsed = parseCustomCron(cron);
  if (!parsed) {
    // Casos especiales: cada hora, cada N horas
    const parts = cron.trim().split(/\s+/);
    if (parts.length === 5) {
      const [mm, hh] = parts;
      if (mm === "0" && hh === "*") return "Cada hora a los :00";
      if (mm === "0" && hh.startsWith("*/")) {
        const step = hh.slice(2);
        return `Cada ${step} horas (00:00, ${step}:00, …)`;
      }
    }
    return cron ? `Cron crudo: ${cron}` : "—";
  }
  const { days, hh, mm } = parsed;
  const dayText = days.every(Boolean)
    ? "todos los días"
    : days
        .map((d, i) => (d ? DAY_NAMES[i] : null))
        .filter((d): d is string => d !== null)
        .join(", ");
  const hhStr = hh.toString().padStart(2, "0");
  const mmStr = mm.toString().padStart(2, "0");
  return `${dayText.charAt(0).toUpperCase()}${dayText.slice(1)} a las ${hhStr}:${mmStr}`;
}

export function ScheduleBuilder({ value, onChange }: ScheduleBuilderProps) {
  // Modo inicial: si value coincide con un preset, ese; si no, "custom".
  const matchedPreset = useMemo(
    () => PRESETS.find((p) => p.cron === value),
    [value],
  );
  const [mode, setMode] = useState<"preset" | "custom">(
    matchedPreset ? "preset" : "custom",
  );

  // Estado del modo custom (días + hora). Si arranca en "custom", parsea value.
  const parsedInitial = useMemo(() => parseCustomCron(value), []);
  const [days, setDays] = useState<boolean[]>(
    parsedInitial?.days ?? [true, false, false, false, false, false, false],
  );
  const [hh, setHh] = useState<number>(parsedInitial?.hh ?? 3);
  const [mm, setMm] = useState<number>(parsedInitial?.mm ?? 0);

  // En modo custom, emite el cron generado al parent cuando cambian días/hora.
  useEffect(() => {
    if (mode === "custom") {
      const cron = buildCron(days, hh, mm);
      if (cron && cron !== value) onChange(cron);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, days, hh, mm]);

  const toggleDay = (idx: number) => {
    setDays((prev) => prev.map((d, i) => (i === idx ? !d : d)));
  };

  const customValid = days.some(Boolean);

  return (
    <div className="space-y-3">
      {/* Tabs presets */}
      <div className="flex flex-wrap gap-2">
        {PRESETS.map((p) => {
          const active = mode === "preset" && value === p.cron;
          return (
            <button
              key={p.cron}
              type="button"
              onClick={() => {
                setMode("preset");
                onChange(p.cron);
              }}
              className={cn(
                "rounded-md border px-3 py-2 text-xs font-medium transition-colors",
                active
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-border hover:bg-accent",
              )}
              title={p.hint}
            >
              {p.label}
            </button>
          );
        })}
        <button
          type="button"
          onClick={() => setMode("custom")}
          className={cn(
            "rounded-md border px-3 py-2 text-xs font-medium transition-colors",
            mode === "custom"
              ? "border-primary bg-primary/10 text-primary"
              : "border-border hover:bg-accent",
          )}
        >
          Personalizado
        </button>
      </div>

      {/* Modo custom: días + hora */}
      {mode === "custom" && (
        <div className="space-y-3 rounded-md border bg-muted/30 p-3">
          <div>
            <div className="mb-2 text-xs font-medium text-muted-foreground">
              Días de la semana
            </div>
            <div className="flex flex-wrap gap-2">
              {DAY_LABELS.map((d, i) => {
                const active = days[i];
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => toggleDay(i)}
                    aria-label={DAY_NAMES[i]}
                    aria-pressed={active}
                    className={cn(
                      "size-9 rounded-md border text-sm font-medium transition-colors",
                      active
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border hover:bg-accent",
                    )}
                  >
                    {d}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <div className="mb-2 text-xs font-medium text-muted-foreground">
              Hora del día
            </div>
            <div className="flex items-center gap-2">
              <select
                aria-label="Hora"
                value={hh}
                onChange={(e) => setHh(parseInt(e.target.value, 10))}
                className="rounded-md border bg-background px-2 py-1.5 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              >
                {Array.from({ length: 24 }, (_, i) => (
                  <option key={i} value={i}>
                    {i.toString().padStart(2, "0")}
                  </option>
                ))}
              </select>
              <span className="font-mono">:</span>
              <select
                aria-label="Minuto"
                value={mm}
                onChange={(e) => setMm(parseInt(e.target.value, 10))}
                className="rounded-md border bg-background px-2 py-1.5 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              >
                {[0, 15, 30, 45].map((m) => (
                  <option key={m} value={m}>
                    {m.toString().padStart(2, "0")}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {!customValid && (
            <p className="text-xs text-destructive">
              Selecciona al menos un día.
            </p>
          )}
        </div>
      )}

      {/* Preview legible + cron raw para auditoría */}
      <div className="flex items-start gap-2 rounded-md bg-muted/30 px-3 py-2 text-xs">
        <Clock className="mt-0.5 size-3 shrink-0 text-muted-foreground" />
        <div className="flex-1">
          <div className="font-medium">{describeCron(value)}</div>
          <div className="font-mono text-muted-foreground">{value || "—"}</div>
        </div>
      </div>
    </div>
  );
}
